import { Dropdown, IDropdownOption, Stack, Text, TextField } from '@fluentui/react';
import React, { useState, useEffect, useContext } from 'react';
import { IFlatTraitDef, QueueConfiguration } from 'services';
import { AdminContext } from 'Store/Admin';
import { BaseTab } from './BaseTab';
import { useLocation } from 'react-router-dom';
interface TraitsTabProps {
  queueData: QueueConfiguration;
}
export enum TraitDetails {
  Category = 'category',
  Trait = 'trait'
}
interface UpdateTrait {
  category: string;
  trait: string;
  definition?: string;
  examples?: string[];
  exemptions?: string[];
}

export const PolicyDefinitionsTab: React.FC<TraitsTabProps> = props => {
  const { dispatch } = useContext(AdminContext);
  const maxDefinitionLength = 750;
  const [formState, setFormState] = useState<UpdateTrait>({
    category: '',
    trait: '',
    definition: '',
    examples: [],
    exemptions: []
  });
  const [dropdownCategoryOptions, setDropdownCategoryIdOptions] = useState<IDropdownOption[]>([]);
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const queryCategory = queryParams.get(TraitDetails.Category);
  const queryTrait = queryParams.get(TraitDetails.Trait);
  const propShortPath = props.queueData.categorizedTraitDefinitions;

  useEffect(() => {
    if (queryCategory && queryTrait) {
      setFormState({
        category: queryCategory,
        trait: queryTrait,
        definition: propShortPath?.[queryCategory]?.[queryTrait]?.definition,
        examples: propShortPath?.[queryCategory]?.[queryTrait]?.examples,
        exemptions: propShortPath?.[queryCategory]?.[queryTrait]?.exemptions
      });
    }
    queryParams.delete(TraitDetails.Category);
    queryParams.delete(TraitDetails.Trait);
    createDropdownCategoryOptions();
  }, []);

  const createDropdownCategoryOptions = () => {
    const dropdownCategoryOptions: IDropdownOption[] = Object.keys(props.queueData.categorizedTraits).map(title => {
      return { key: title, text: title };
    });
    setDropdownCategoryIdOptions(dropdownCategoryOptions);
  };

  const onSave = (currentFormState: UpdateTrait) => {
    dispatch({
      type: 'TRAIT_DEFINITION',
      payload: currentFormState as IFlatTraitDef
    });

    dispatch({
      type: 'IS_UPDATED',
      payload: true
    });
  };

  useEffect(() => {
    if (formState.category && formState.trait && formState.definition) {
      onSave(formState);
    }
  }, [formState, formState.examples?.length]);

  useEffect(() => {
    if (queryCategory && queryTrait) {
      setFormState(old => ({ ...old, category: queryCategory, trait: queryTrait }));
    }
  }, []);

  return (
    <BaseTab>
      <Stack>
        <Stack.Item>
          <Stack.Item>
            <Dropdown
              label={'Category'}
              placeholder={'Select a Category'}
              options={dropdownCategoryOptions}
              onChange={(e, category) => setFormState({ category: category?.text ?? '', trait: '' })}
              required
              selectedKey={formState.category}
            />
          </Stack.Item>
          <Stack.Item>
            <Dropdown
              label={'Trait'}
              placeholder={'Select a Trait'}
              options={
                formState.category
                  ? props.queueData.categorizedTraits[formState.category].map(title => {
                      return { key: title, text: title };
                    })
                  : []
              }
              onChange={(e, trait) =>
                setFormState({
                  ...propShortPath?.[formState.category]?.[trait?.text || ''],
                  category: formState.category ?? '',
                  trait: trait?.text ?? ''
                })
              }
              required
              disabled={!formState.category}
              selectedKey={formState.trait}
            />
          </Stack.Item>
        </Stack.Item>
        <Stack.Item grow className="mt-4">
          <div role="heading" aria-level={2}>
            <Text variant="mediumPlus">Trait Definition</Text>
          </div>
        </Stack.Item>
        <Stack.Item grow>
          <Text>{`Enter a description for the selected trait.`}</Text>
          <TextField
            label="description"
            rows={5}
            type="textarea"
            disabled={!formState.trait}
            maxLength={maxDefinitionLength}
            onChange={(e, definition) => setFormState(old => ({ ...old, definition: definition ?? '' }))}
            multiline
            resizable={false}
            autoAdjustHeight
            required
            value={formState.definition}
          />
        </Stack.Item>
        <Stack.Item grow className="mt-4">
          <div role="heading" aria-level={2}>
            <Text variant="mediumPlus">Trait Example</Text>
          </div>
        </Stack.Item>
        <Stack.Item grow>
          <Text>{`Enter an example  separated by a comma ( , ).`}</Text>
          <TextField
            label="examples"
            rows={5}
            type="textarea"
            disabled={!formState.trait}
            maxLength={maxDefinitionLength}
            onChange={(e, exam) => setFormState(old => ({ ...old, examples: (exam || '').split(', ') }))}
            multiline
            resizable={false}
            autoAdjustHeight
            value={formState.examples?.join(', ')}
          />
        </Stack.Item>
        <Stack.Item grow className="mt-1">
          <ul>
            {formState.examples?.map(example => (
              <li key={example}>{example}</li>
            ))}
          </ul>
        </Stack.Item>
        <Stack.Item grow className="mt-2">
          <div role="heading" aria-level={2}>
            <Text variant="mediumPlus">{`Trait Exemptions `} </Text>
          </div>
        </Stack.Item>
        <Stack.Item grow>
          <Text>{`Enter an exemption separated by a comma ( , ).`}</Text>
          <TextField
            label="exemptions"
            rows={5}
            type="textarea"
            disabled={!formState.trait}
            maxLength={maxDefinitionLength}
            onChange={(e, exempt) => setFormState(old => ({ ...old, exemptions: (exempt || '').split(', ') }))}
            multiline
            resizable={false}
            autoAdjustHeight
            value={formState.exemptions?.join(', ')}
          />
        </Stack.Item>
        <Stack.Item grow className="mt-1">
          <ul>
            {formState.exemptions?.map(exemption => (
              <li key={exemption}>{exemption}</li>
            ))}
          </ul>
        </Stack.Item>
      </Stack>
    </BaseTab>
  );
};
